import { VERT } from '@somnhold/content';
import { hexLen, hexToWorld, worldToHex, type Hex } from '../hex.ts';
import { idiv, imax, imin, isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng, type RngState } from '../math/rng.ts';
import type { Pt } from '../caza-types.ts';

/**
 * The Long Hunt's chapter 2 grounds from the seed (docs/sendas/barbaro.md, «Las Vértebras»): the arches in the wind
 * (act 1), the leaps (act 2, with its solver: §4 «Los arcos») and the pack's den on the last vertebra (act 3). The
 * fight's builder (campaign/sendas-caza.ts) and the renderer build them the same way. Integer maths only.
 */

export const ARCHES_RADIUS = 16;
export const LEAPS_RADIUS = 12;
export const DEN_RADIUS = 11;
/** The den's floor: the vertebra's top, this many hexes from its middle; the cave's mound north of row −6. */
export const DEN_FLOOR = 8;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

const len = (dx: number, dy: number): number => isqrt(dx * dx + dy * dy);

/** The squared distance from a point to a segment (integer, the segment's parameter in bp). */
const segDist = (p: Pt, a: Pt, b: Pt): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? idiv(((p.x - a.x) * dx + (p.y - a.y) * dy) * 100, idiv(l2, 100)) : 0;
  t = imax(0, imin(10000, t));
  const x = a.x + idiv(dx * t, 10000);
  const y = a.y + idiv(dy * t, 10000);
  return (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
};

/** The board's rocks: every cell of the map (its radius + 2) that is not floor. */
const rocksBut = (R: number, floor: Set<string>): Hex[] => allCells(R + 2).filter((h) => !floor.has(`${h.q},${h.r}`));

/* ---------- the arches in the wind (act 1) ---------- */

export interface ArchesLayout {
  rocks: Hex[];
  floor: Hex[];
  heads: Pt[];
  posts: Pt[];
  path: Pt[];
  mark: number;
  balk: number;
  over: number;
  start: Pt;
  end: Pt;
  plan: { t: number; dir: number }[];
}

/** Points every `step` along a polyline. */
const along = (ctl: Pt[], step: number): Pt[] => {
  const out: Pt[] = [{ ...(ctl[0] as Pt) }];
  for (let i = 1; i < ctl.length; i++) {
    const a = ctl[i - 1] as Pt;
    const b = ctl[i] as Pt;
    const d = len(b.x - a.x, b.y - a.y);
    const n = imax(1, idiv(d, step));
    for (let k = 1; k <= n; k++) out.push({ x: a.x + idiv((b.x - a.x) * k, n), y: a.y + idiv((b.y - a.y) * k, n) });
  }
  return out;
};

export const archesLayout = (seed: number): ArchesLayout => {
  const rng = seedRng(deriveSeed(seed, 0xa4c5));
  const A = VERT.arches;
  const n = A.arches;
  // The heads south to north, swinging from side to side as the spine goes under (the first and last wider).
  const heads: Pt[] = [];
  const y0 = 12_400;
  const y1 = -12_200;
  let side = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let i = 0; i < n; i++) {
    const y = y0 + idiv((y1 - y0) * i, n - 1);
    const sway = i === 0 || i === n - 1 ? 0 : side * rngRange(rng, 2800, 4600);
    side = -side;
    const c = worldToHex(sway, y);
    heads.push(hexToWorld(c.q, c.r));
  }
  const floor = new Set<string>();
  const cells = allCells(ARCHES_RADIUS);
  for (const h of cells) {
    const p = hexToWorld(h.q, h.r);
    let ok = false;
    heads.forEach((c, i) => {
      const big = i === 0 || i === n - 1 ? 1800 : 1100;
      if ((p.x - c.x) * (p.x - c.x) + (p.y - c.y) * (p.y - c.y) <= big * big) ok = true;
    });
    for (let i = 1; i < n && !ok; i++) if (segDist(p, heads[i - 1] as Pt, heads[i] as Pt) <= 600 * 600) ok = true;
    if (ok) floor.add(`${h.q},${h.r}`);
  }
  // A mooring post at each head, on the side the way swings away from (the lee of the bend).
  const posts: Pt[] = heads.map((c, i) => {
    const nx = (heads[imin(n - 1, i + 1)] as Pt).x;
    const px = (heads[imax(0, i - 1)] as Pt).x;
    const off = nx + px - 2 * c.x >= 0 ? -1 : 1;
    const h = worldToHex(c.x + off * 1000, c.y);
    const p = hexToWorld(h.q, h.r);
    return floor.has(`${h.q},${h.r}`) ? p : { ...c };
  });
  const path = along(heads, 400);
  // Terrón balks at the gap after the `balk`-th head: the path's point a third of the way along the span.
  const at = (i: number): number => {
    let best = 0;
    let bd = 0x7fffffff;
    const c = heads[i] as Pt;
    path.forEach((p, k) => {
      const d = len(p.x - c.x, p.y - c.y);
      if (d < bd) {
        bd = d;
        best = k;
      }
    });
    return best;
  };
  const b0 = at(A.balk);
  const b1 = at(A.balk + 1);
  const balk = b0 + idiv(b1 - b0, 3);
  const over = b0 + idiv((b1 - b0) * 2, 3);
  // The gusts, side by side from the seed (never three the same way running).
  const plan: { t: number; dir: number }[] = [];
  let t = A.gust.first;
  let last = 0;
  let run = 0;
  for (let k = 0; k < 60; k++) {
    let dir = rngInt(rng, 2) === 0 ? -1 : 1;
    if (dir === last && run >= 2) dir = -dir;
    run = dir === last ? run + 1 : 1;
    last = dir;
    plan.push({ t, dir });
    t += rngRange(rng, A.gust.every[0], A.gust.every[1]);
  }
  const floorList = cells.filter((h) => floor.has(`${h.q},${h.r}`));
  return {
    rocks: rocksBut(ARCHES_RADIUS, floor),
    floor: floorList,
    heads,
    posts,
    path,
    mark: imin(n - 2, 5),
    balk,
    over,
    start: { ...(heads[0] as Pt), y: (heads[0] as Pt).y + 500 },
    end: { ...(heads[n - 1] as Pt) },
    plan,
  };
};

/* ---------- the leaps (act 2) ---------- */

/** A lattice point of the leaps (i, j): x = 4000 i + 2000 j, a row j every 3464 north of the first. */
const LY0 = 6928;
const lat = (i: number, j: number): Pt => {
  const x = 4000 * i + 2000 * j;
  const y = LY0 - 3464 * j;
  const h = worldToHex(x, y);
  return hexToWorld(h.q, h.r);
};
const LAT_N: [number, number][] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, -1],
  [-1, 1],
];

export interface LeapsGraph {
  /** Lattice coordinates of the ten arches, then the vertebra. */
  at: [number, number][];
  edges: [number, number][];
  marks: number[];
  start: number;
}

/**
 * The spec's own example (§4, «Ejemplo verificado»), its arches 1 to 10 here 0 to 9 and the vertebra 10: gaps 1–2, 2–3,
 * 3–4, 4–V, 2–5, 3–6, 3–8, 6–7, 7–4, 4–9, 9–10; marks on 5, 6 and 8.
 */
export const LEAPS_EXAMPLE: LeapsGraph = {
  at: [
    [0, 0],
    [-1, 1],
    [-1, 2],
    [-1, 3],
    [-2, 1],
    [0, 2],
    [0, 3],
    [-2, 2],
    [-2, 3],
    [-3, 3],
    [-2, 4],
  ],
  edges: [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 10],
    [1, 4],
    [2, 5],
    [2, 7],
    [5, 6],
    [3, 6],
    [3, 8],
    [8, 9],
  ],
  marks: [4, 5, 7],
  start: 0,
};

const norm = (e: [number, number][]): [number, number][] =>
  e.map(([a, b]) => (a < b ? [a, b] : [b, a]) as [number, number]).sort((p, q) => p[0] - q[0] || p[1] - q[1]);

/** The neighbours of each arch by its gaps. */
export const leapsAdj = (n: number, edges: [number, number][]): number[][] => {
  const adj: number[][] = Array.from({ length: n }, () => []);
  for (const [a, b] of edges) {
    (adj[a] as number[]).push(b);
    (adj[b] as number[]).push(a);
  }
  for (const l of adj) l.sort((a, b) => a - b);
  return adj;
};

/**
 * A puzzle's position: where the hero is, each arch's arrivals (3: fallen), the marks touched (a bit each, in the
 * marks' order), and which arches the marks are on.
 */
export interface LeapsPos {
  on: number;
  cracks: number[];
  touched: number;
  marks: number[];
}

/** The moves from a position: to a standing neighbour (the vertebra is never cracked). */
const movesOf = (adj: number[][], v: number, p: LeapsPos): number[] => (adj[p.on] as number[]).filter((k) => k === v || (p.cracks[k] as number) < 3);

const arrive = (v: number, p: LeapsPos, k: number): LeapsPos | null => {
  if (k === v) return { ...p, on: k };
  const c = (p.cracks[k] as number) + 1;
  // The third arrival brings it down: the hero back where it was (a dead end for the search: no mark moves here).
  if (c >= 3) return null;
  const cracks = p.cracks.slice();
  cracks[k] = c;
  let touched = p.touched;
  p.marks.forEach((m, i) => {
    if (m === k) touched |= 1 << i;
  });
  return { on: k, cracks, touched, marks: p.marks };
};

/**
 * Every way through from a position to the vertebra with every mark touched, without bringing an arch down (`cap` at
 * most, each the arches in the order walked). The first is the shortest found by depth (not by length).
 */
export const leapsRoutes = (adj: number[][], p0: LeapsPos, cap: number): number[][] => {
  const v = adj.length - 1;
  const all = (1 << p0.marks.length) - 1;
  const out: number[][] = [];
  const way: number[] = [p0.on];
  const go = (p: LeapsPos): void => {
    if (out.length >= cap) return;
    if (p.on === v) {
      if (p.touched === all) out.push(way.slice());
      return;
    }
    for (const k of movesOf(adj, v, p)) {
      const q = arrive(v, p, k);
      if (!q) continue;
      way.push(k);
      go(q);
      way.pop();
      if (out.length >= cap) return;
    }
  };
  go(p0);
  return out;
};

/** The shortest way through from a position (breadth first over positions), or null: the bot's and the stuck check's. */
export const leapsSolve = (adj: number[][], p0: LeapsPos, limit = 60_000): number[] | null => {
  const v = adj.length - 1;
  const all = (1 << p0.marks.length) - 1;
  const key = (p: LeapsPos): string => `${p.on}|${p.touched}|${p.cracks.join('')}`;
  const seen = new Set<string>([key(p0)]);
  const queue: { p: LeapsPos; way: number[] }[] = [{ p: p0, way: [p0.on] }];
  for (let h = 0; h < queue.length && h < limit; h++) {
    const { p, way } = queue[h] as { p: LeapsPos; way: number[] };
    if (p.on === v) {
      if (p.touched === all) return way;
      continue;
    }
    for (const k of movesOf(adj, v, p)) {
      const q = arrive(v, p, k);
      if (!q) continue;
      const kk = key(q);
      if (seen.has(kk)) continue;
      seen.add(kk);
      queue.push({ p: q, way: [...way, k] });
    }
  }
  return null;
};

/** The order the marks are touched in along a way (their indices in `marks`). */
export const markOrder = (marks: number[], way: number[]): number[] => {
  const out: number[] = [];
  for (const k of way) {
    const i = marks.indexOf(k);
    if (i >= 0 && !out.includes(i)) out.push(i);
  }
  return out;
};

/** The shortest way from one arch to another by the gaps alone (no arrivals counted), or []. */
const bfs = (adj: number[][], a: number, b: number): number[] => {
  const prev = adj.map(() => -2);
  prev[a] = -1;
  const q = [a];
  for (let h = 0; h < q.length; h++) {
    const x = q[h] as number;
    if (x === b) break;
    for (const y of adj[x] as number[])
      if (prev[y] === -2) {
        prev[y] = x;
        q.push(y);
      }
  }
  if (prev[b] === -2) return [];
  const out: number[] = [];
  for (let x = b; x !== -1; x = prev[x] as number) out.unshift(x);
  return out;
};

/**
 * Whether a layout is a good puzzle (§4): one to a dozen ways through, all touching the marks in one order; the short
 * way to the vertebra misses a mark; a mark is a dead end; the way through takes nine leaps at least.
 */
export const leapsGood = (g: LeapsGraph): boolean => {
  const n = g.at.length;
  const adj = leapsAdj(n, g.edges);
  const v = n - 1;
  if ((adj[v] as number[]).length !== 1) return false;
  if (adj.some((l) => l.length === 0)) return false;
  const cracks = adj.map(() => 0);
  cracks[g.start] = 1;
  const routes = leapsRoutes(adj, { on: g.start, cracks, touched: 0, marks: g.marks }, 13);
  if (routes.length < 1 || routes.length > 12) return false;
  const o = markOrder(g.marks, routes[0] as number[]).join(',');
  if (routes.some((r) => markOrder(g.marks, r).join(',') !== o)) return false;
  if (routes.some((r) => r.length < 10)) return false;
  const short = bfs(adj, g.start, v);
  if (g.marks.every((m) => short.includes(m))) return false;
  if (!g.marks.some((m) => (adj[m] as number[]).length === 1)) return false;
  // What is obvious is wrong: going for the nearest mark left each time (by the fewest gaps) doesn't get through.
  if (leapsGreedy(adj, g.start, g.marks)) return false;
  return true;
};

/**
 * The obvious way (§4, «Por qué lo obvio está mal»): from where it stands, by the fewest gaps over what still stands to
 * the nearest mark left, then the vertebra, arrivals uncounted; whether that gets through.
 */
export const leapsGreedy = (adj: number[][], start: number, marks: number[]): boolean => {
  const v = adj.length - 1;
  const cracks = adj.map(() => 0);
  cracks[start] = 1;
  const left = marks.slice();
  let on = start;
  for (let step = 0; step < 60; step++) {
    if (on === v) return left.length === 0;
    const want = left.length ? left : [v];
    const prev = adj.map(() => -2);
    prev[on] = -1;
    const q = [on];
    let hit = -1;
    for (let k = 0; k < q.length && hit < 0; k++) {
      const a = q[k] as number;
      for (const b of adj[a] as number[]) {
        if (prev[b] !== -2 || (b !== v && (cracks[b] as number) >= 3)) continue;
        prev[b] = a;
        if (want.includes(b)) {
          hit = b;
          break;
        }
        q.push(b);
      }
    }
    if (hit < 0) return false;
    let x = hit;
    while (prev[x] !== on) x = prev[x] as number;
    if (x !== v) {
      cracks[x] = (cracks[x] as number) + 1;
      if ((cracks[x] as number) >= 3) return false;
    }
    on = x;
    const i = left.indexOf(x);
    if (i >= 0) left.splice(i, 1);
  }
  return false;
};

/** A layout from the seed: arches picked on the lattice, gaps kept at random, marks placed; checked by leapsGood. */
const leapsTry = (rng: RngState): LeapsGraph | null => {
  const pool: [number, number][] = [];
  for (let j = 0; j <= 3; j++)
    for (let i = -4; i <= 3; i++) {
      const x = 4000 * i + 2000 * j;
      if (x >= -6500 && x <= 6500) pool.push([i, j]);
    }
  // A start in the first row, nine more, the vertebra north of one of the last row's.
  const row0 = pool.filter((p) => p[1] === 0);
  const start = row0[rngInt(rng, row0.length)] as [number, number];
  const rest = pool.filter((p) => p !== start);
  for (let i = rest.length - 1; i > 0; i--) {
    const k = rngInt(rng, i + 1);
    [rest[i], rest[k]] = [rest[k] as [number, number], rest[i] as [number, number]];
  }
  const at: [number, number][] = [start, ...rest.slice(0, 9)];
  const last = at.filter((p) => p[1] === 3);
  if (!last.length) return null;
  const top = last[rngInt(rng, last.length)] as [number, number];
  const vx = rngInt(rng, 2) === 0 ? [top[0], 4] : [top[0] - 1, 4];
  const vxy = lat(vx[0] as number, 4);
  if (vxy.x < -5000 || vxy.x > 5000) return null;
  at.push([vx[0] as number, 4]);
  const n = at.length;
  const idx = (i: number, j: number): number => at.findIndex((p) => p[0] === i && p[1] === j);
  const edges: [number, number][] = [];
  for (let a = 0; a < n - 1; a++)
    for (const [di, dj] of LAT_N) {
      const b = idx((at[a] as [number, number])[0] + di, (at[a] as [number, number])[1] + dj);
      if (b <= a || b === n - 1) continue;
      if (rngInt(rng, 100) < 55) edges.push([a, b]);
    }
  edges.push([at.indexOf(top), n - 1]);
  const marks: number[] = [];
  while (marks.length < 3) {
    const m = 1 + rngInt(rng, n - 2);
    if (!marks.includes(m)) marks.push(m);
  }
  marks.sort((a, b) => a - b);
  const g = { at, edges: norm(edges), marks, start: 0 };
  return leapsGood(g) ? g : null;
};

export interface LeapsLayout {
  rocks: Hex[];
  graph: LeapsGraph;
  arches: Pt[];
  /** Each arch's floor cells (the vertebra's wider). */
  floor: Hex[][];
  start: Pt;
  example: boolean;
}

/** The floor radius of an arch (hexes); the vertebra's. */
export const LEAP_ARCH = 1;
export const LEAP_VERT = 2;

export const leapsLayoutOf = (g: LeapsGraph, example: boolean): LeapsLayout => {
  const arches = g.at.map(([i, j]) => lat(i, j));
  const floor: Hex[][] = arches.map((c, k) => {
    const h = worldToHex(c.x, c.y);
    const R = k === arches.length - 1 ? LEAP_VERT : LEAP_ARCH;
    return allCells(R).map((d) => ({ q: h.q + d.q, r: h.r + d.r }));
  });
  const set = new Set<string>();
  for (const f of floor) for (const h of f) set.add(`${h.q},${h.r}`);
  const s0 = arches[g.start] as Pt;
  return { rocks: rocksBut(LEAPS_RADIUS, set), graph: g, arches, floor, start: { x: s0.x, y: s0.y + 300 }, example };
};

/** The leaps from the seed: a good layout of its own, else the spec's example (mirrored half the time). */
export const leapsLayout = (seed: number): LeapsLayout => {
  const rng = seedRng(deriveSeed(seed, 0x1ea9));
  for (let k = 0; k < VERT.leaps.tries; k++) {
    const g = leapsTry(rng);
    if (g) return leapsLayoutOf(g, false);
  }
  // Mirrored: x → −x on the lattice is (i, j) → (−i − j, j).
  const flip = rngInt(rng, 2) === 1;
  const g: LeapsGraph = flip ? { ...LEAPS_EXAMPLE, at: LEAPS_EXAMPLE.at.map(([i, j]) => [-i - j, j] as [number, number]) } : LEAPS_EXAMPLE;
  return leapsLayoutOf(g, true);
};

/* ---------- the den (act 3) ---------- */

export interface DenLayout {
  rocks: Hex[];
  mouth: Pt;
  perches: Pt[];
  boat: Pt;
  bera: Pt;
  terron: Pt;
  sten: Pt;
  start: Pt;
  slabs: { cells: Hex[]; at: Pt }[];
  stones: Pt[];
  rolls: Pt[];
  /** The cave's mound (rock cells inside the floor's ring, for the drawing). */
  cave: Hex[];
}

export const denLayout = (seed: number): DenLayout => {
  const rng = seedRng(deriveSeed(seed, 0xde77));
  const F = DEN_FLOOR;
  const floor = new Set<string>();
  const cave: Hex[] = [];
  for (const h of allCells(F)) {
    // The cave's mound: the north rows, its mouth open in the middle of row −6.
    if (h.r <= -7) {
      cave.push(h);
      continue;
    }
    floor.add(`${h.q},${h.r}`);
  }
  // The boat's slip at the south edge: a tongue of rock down to the water.
  for (const q of [-4, -3]) floor.add(`${q},${F + 1}`);
  const mouth = hexToWorld(3, -6);
  const boat = hexToWorld(-4, F + 1);
  const bera = hexToWorld(-4, F);
  const terron = hexToWorld(-3, F);
  const sten = hexToWorld(-5, F);
  // The slabs: six of three cells, in the ring between two and six hexes out, apart from each other, the mouth and the slip.
  const slabs: { cells: Hex[]; at: Pt }[] = [];
  const used = new Set<string>();
  const near = (h: Hex, p: Hex, d: number): boolean => hexLen(h.q - p.q, h.r - p.r) <= d;
  for (let k = 0; k < 400 && slabs.length < VERT.den.slabs; k++) {
    const q = rngRange(rng, -6, 6);
    const r = rngRange(rng, -5, 6);
    const d = hexLen(q, r);
    if (d < 2 || d > 6) continue;
    const c = { q, r };
    const tri = [c, { q: q + 1, r }, { q, r: r + 1 }];
    if (tri.some((h) => !floor.has(`${h.q},${h.r}`) || used.has(`${h.q},${h.r}`) || near(h, { q: 3, r: -6 }, 2) || near(h, { q: -4, r: F }, 2))) continue;
    if (slabs.some((sl) => sl.cells.some((h) => tri.some((t) => near(h, t, 2))))) continue;
    for (const h of tri) used.add(`${h.q},${h.r}`);
    const ps = tri.map((h) => hexToWorld(h.q, h.r));
    slabs.push({ cells: tri, at: { x: idiv(ps[0]!.x + ps[1]!.x + ps[2]!.x, 3), y: idiv(ps[0]!.y + ps[1]!.y + ps[2]!.y, 3) } });
  }
  // The howlers' places either side of the mouth; the loose stones: two on the cave's shoulders rolling along its foot
  // (across both places), two on the rim west and east rolling in toward the middle (the pack's way to the boat).
  const perches = [hexToWorld(0, -5), hexToWorld(5, -5)];
  const stones = [hexToWorld(-3, -5), hexToWorld(8, -6), hexToWorld(-7, 2), hexToWorld(7, -2)];
  const rolls = [
    { x: 1000, y: 0 },
    { x: -1000, y: 0 },
    { x: 1000, y: 0 },
    { x: -1000, y: 0 },
  ];
  return { rocks: rocksBut(DEN_RADIUS, floor), mouth, perches, boat, bera, terron, sten, start: hexToWorld(-1, 3), slabs, stones, rolls, cave };
};
